import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'

import { cn } from '@/utils/cn'
import { usePortalContainer } from '@/components/ui/ShadcnProvider'

/**
 * Tooltip Component
 *
 * Displays a short hint on hover or focus.
 *
 * @see https://ui.shadcn.com/docs/components/base/tooltip
 *
 * @example
 * ```tsx
 * <Tooltip>
 *   <TooltipTrigger>Hover me</TooltipTrigger>
 *   <TooltipContent>Tooltip text</TooltipContent>
 * </Tooltip>
 * ```
 *
 * @remarks
 * Key Props:
 * - TooltipProvider: `delay`
 * - TooltipContent: `side`, `sideOffset`, `align`, `alignOffset`
 * - Root / Trigger: see Base UI
 */

function TooltipProvider({ delay = 0, ...props }: TooltipPrimitive.Provider.Props) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delay} {...props} />
}

type TooltipDelayProps = {
  /** Hover open delay in ms (forwarded to the provider). Defaults to 0. */
  delay?: number
  /** Close delay in ms (forwarded to the provider). */
  closeDelay?: number
}

function Tooltip({ delay, closeDelay, ...props }: TooltipPrimitive.Root.Props & TooltipDelayProps) {
  return (
    <TooltipProvider delay={delay} closeDelay={closeDelay}>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  )
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  side = 'top',
  // The arrow tip protrudes ~4px past the popup edge, so the DS's 4px visual gap
  // between tooltip and trigger needs an 8px offset on the popup box.
  sideOffset = 8,
  align = 'center',
  alignOffset = 0,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<TooltipPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'>) {
  const portalContainer = usePortalContainer()
  return (
    <TooltipPrimitive.Portal container={portalContainer}>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-[var(--z-overlay)]"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            /* The tooltip is the app's only inverted text surface, so it owns what a link looks like on
               its fill: `Link`'s default `text-primary` is #171717 on #0a0a0a in light mode and
               #12ff80 on #f5f5f5 in dark — both unreadable. `text-background` is the other half of
               the tooltip's own contrast pair, so it clears AA either way, and the underline carries
               the affordance. Hover is pinned to the same token because `Link`'s own hover colour
               (--color-primary-light) is a mid grey that drops to ~3:1 on this fill in light mode and
               worse in dark. `:not([data-slot=button])` leaves `ExternalLink mode="button"` alone. */
            '[&_a:not([data-slot=button])]:text-background [&_a:not([data-slot=button])]:hover:text-background [&_a:not([data-slot=button])]:underline [&_a:not([data-slot=button])]:decoration-background/60 [&_a:not([data-slot=button])]:hover:decoration-current',
            'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 rounded-md px-3 py-1.5 text-xs data-[side=inline-start]:slide-in-from-right-2 data-[side=inline-end]:slide-in-from-left-2 bg-foreground text-background z-[var(--z-overlay)] w-fit max-w-xs origin-(--transform-origin)',
            className,
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow className="size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] data-[side=inline-start]:top-1/2! data-[side=inline-start]:-right-1 data-[side=inline-start]:-translate-y-1/2 data-[side=inline-end]:top-1/2! data-[side=inline-end]:-left-1 data-[side=inline-end]:-translate-y-1/2 bg-foreground fill-foreground z-[var(--z-overlay)] data-[side=bottom]:top-1 data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5" />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
